import React, { useEffect, useState } from "react";
import Logo from "./Logo";
import ParticleCanvas from './ParticleCanvas';

const BlogPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const scriptId = "dropinblog-embed";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src = "https://io.dropinblog.com/embedjs/ca3cd16d-f9f2-4aa2-80a6-6f62480a79a2.js";
      document.body.appendChild(script);
    }

    const postsRoot = document.getElementById("dib-posts");
    const postRoot = document.getElementById("dib-post");

    const resolveLoading = () => {
      const postsHasContent = postsRoot?.children?.length;
      const postHasContent = postRoot?.children?.length;
      if (postsHasContent || postHasContent) {
        setIsLoading(false);
      }
    };

    resolveLoading();

    const observer = new MutationObserver(resolveLoading);
    if (postsRoot) {
      observer.observe(postsRoot, { childList: true, subtree: true });
    }
    if (postRoot) {
      observer.observe(postRoot, { childList: true, subtree: true });
    }

    const handleBlogNav = () => {
      setIsLoading(true);
      setTimeout(resolveLoading, 500);
    };

    window.addEventListener("popstate", handleBlogNav);

    const reloadKey = "dib_reload_once";
    const ensureContentTimer = setTimeout(() => {
      if (!postsRoot?.children?.length && !postRoot?.children?.length) {
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, "1");
          window.location.reload();
        }
      }
    }, 1500);

    return () => {
      observer.disconnect();
      window.removeEventListener("popstate", handleBlogNav);
      clearTimeout(ensureContentTimer);
    };
  }, []);

  return (
    <div className="min-h-screen font-sans text-slate-100 bg-slate-950 selection:bg-yellow-500/30">
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-x-0 -top-40 h-[500px] bg-yellow-500/10 blur-[120px] rounded-full opacity-50" />
        <div className="absolute bottom-0 left-0 right-0 h-[500px] bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent z-10" />
        <ParticleCanvas />
      </div>

      {/* Custom Styles for DropInBlog */}
      <style>{`
        :root {
          --dib-primary-color: #38bdf8 !important; /* yellow-400 */
          --dib-heading-color: #f1f5f9 !important; /* slate-100 */
          --dib-body-color: #94a3b8 !important; /* slate-400 */
          --dib-border-color: #1e293b !important; /* slate-800 */
          --dib-bg-color: transparent !important;
          --dib-card-bg: #0f172a !important; /* slate-900 */
        }
        #dib-posts .dib-post-card {
          background-color: rgba(15, 23, 42, 0.6) !important;
          border: 1px solid rgba(30, 41, 59, 1) !important;
          border-radius: 1rem !important;
          padding: 1.5rem !important;
          transition: all 0.3s ease !important;
          backdrop-filter: blur(8px);
        }
        #dib-posts .dib-post-card:hover {
          border-color: rgba(56, 189, 248, 0.5) !important;
          transform: translateY(-2px);
          box-shadow: 0 10px 30px -10px rgba(56, 189, 248, 0.2) !important;
        }
        #dib-posts .dib-post-title a {
          color: #f8fafc !important; /* slate-50 */
          font-weight: 700 !important;
          text-decoration: none !important;
        }
        #dib-posts .dib-post-title a:hover {
          color: #38bdf8 !important; /* yellow-400 */
        }
        #dib-posts .dib-read-more {
          color: #38bdf8 !important;
          font-weight: 600 !important;
        }
        .dib-pagination a, .dib-pagination span {
          background: rgba(30, 41, 59, 0.5) !important;
          color: #cbd5e1 !important;
          border: 1px solid rgba(51, 65, 85, 1) !important;
        }
        .dib-pagination .active {
          background: #38bdf8 !important;
          color: #0f172a !important;
        }
        /* Single Post Styling */
        #dib-post h1, #dib-post h2, #dib-post h3 {
          color: #f1f5f9 !important;
        }
        #dib-post p, #dib-post li {
          color: #cbd5e1 !important;
          line-height: 1.8 !important;
        }
        #dib-post a {
          color: #38bdf8 !important;
        }
        #dib-post img {
          border-radius: 0.75rem !important;
          border: 1px solid #1e293b !important;
        }
        #dib-recent-posts .dib-recent-post-card {
           background-color: rgba(15, 23, 42, 0.6) !important;
           border: 1px solid #1e293b !important;
        }
      `}</style>

      <div className="relative z-10 flex flex-col min-h-screen">
        <header className="fixed w-full z-50 bg-slate-950/80 backdrop-blur-xl shadow-lg border-b border-white/5 py-4 transition-all duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <div
                className="cursor-pointer transform hover:scale-105 transition-transform duration-300"
                onClick={() => { window.location.href = "/"; }}
              >
                <Logo />
              </div>

              <div className="hidden md:flex items-center gap-8">
                <nav className="flex gap-8">
                  <button
                    onClick={() => { window.location.href = "/"; }}
                    className="text-slate-400 hover:text-white font-medium transition-colors text-sm uppercase tracking-wide"
                  >
                    Home
                  </button>
                  <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wide relative">
                    Blog
                    <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-yellow-500 rounded-full shadow-[0_0_10px_rgba(56,189,248,0.5)]"></span>
                  </span>
                </nav>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => { window.location.href = "/"; }}
                    className="px-5 py-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 text-yellow-400 text-sm font-semibold hover:bg-yellow-500 hover:text-white transition-all duration-300 shadow-[0_0_15px_-5px_rgba(56,189,248,0.3)] hover:shadow-[0_0_20px_rgba(56,189,248,0.6)]"
                  >
                    Login / Sign Up
                  </button>
                </div>
              </div>

              <button
                className="md:hidden text-slate-300 hover:text-white p-2"
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              >
                <span className="sr-only">Open menu</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="absolute top-full left-0 w-full bg-slate-900/95 backdrop-blur-xl shadow-xl md:hidden border-t border-slate-800 animate-in slide-in-from-top-2 duration-200">
              <div className="flex flex-col p-6 space-y-4">
                <button
                  onClick={() => { window.location.href = "/"; }}
                  className="text-slate-300 text-left py-3 px-4 hover:bg-white/5 rounded-lg transition-colors border-l-2 border-transparent hover:border-yellow-500"
                >
                  Home
                </button>
                <span className="text-white text-left py-3 px-4 rounded-lg font-semibold bg-white/5 border-l-2 border-yellow-500">Blog</span>
                <button
                  onClick={() => { window.location.href = "/"; }}
                  className="text-center py-3 px-4 border border-slate-700 rounded-lg font-semibold bg-slate-800 text-slate-200"
                >
                  Login / Sign Up
                </button>
              </div>
            </div>
          )}
        </header>

        <main className="flex-grow pt-32 pb-20 relative">
          <section className="relative">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16 relative z-10">
                <div className="inline-block mb-4 px-4 py-1.5 rounded-full border border-yellow-500/20 bg-yellow-500/10 backdrop-blur-sm">
                  <p className="text-yellow-400 uppercase tracking-[0.2em] text-[10px] font-bold">
                    Industry Insights
                  </p>
                </div>
                <h1 className="text-5xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-200 to-slate-400 tracking-tight pb-2 drop-shadow-sm">
                  Our Blog
                </h1>
                <p className="text-slate-400 mt-6 text-xl max-w-2xl mx-auto leading-relaxed font-light">
                  Expert analysis on underwriting automation, lending trends, and the future of digital mortgages.
                </p>
              </div>

              <div
                className="relative bg-slate-900/40 border border-white/5 rounded-3xl p-6 md:p-10 backdrop-blur-md shadow-2xl min-h-[400px]"
                onClickCapture={(event) => {
                  const link = event.target.closest("a");
                  if (link?.href?.includes("cloudrealtor.io/blog")) {
                    event.preventDefault();
                    setIsLoading(true);
                    window.location.href = link.href;
                  }
                }}
              >
                {/* Glow effect behind container */}
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 via-purple-500/20 to-yellow-500/20 blur-xl opacity-30 -z-10 rounded-3xl"></div>

                {isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-50 bg-slate-900/50 backdrop-blur-sm rounded-3xl transition-opacity duration-300">
                    <div className="relative">
                      <div className="w-12 h-12 border-4 border-slate-700 border-t-yellow-500 rounded-full animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="mt-4 text-yellow-400 font-medium tracking-wide text-sm animate-pulse">Loading content...</div>
                  </div>
                )}

                <div id="dib-posts" className="transition-opacity duration-500"></div>
                <div id="dib-post" className="transition-opacity duration-500"></div>
              </div>
            </div>
          </section>
        </main>

        <footer className="bg-slate-950 border-t border-white/5 py-12 relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-sm font-medium text-slate-400 mb-8">
              <a href="/referral-program" className="hover:text-yellow-400 transition-colors duration-200">
                Referral Program
              </a>
              <a href="/terms" className="hover:text-yellow-400 transition-colors duration-200">
                Terms of Service
              </a>
              <a href="/privacy" className="hover:text-yellow-400 transition-colors duration-200">
                Privacy Policy
              </a>
            </div>
            <p className="text-slate-600 text-sm">© {new Date().getFullYear()} CloudRealtor.io. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BlogPage;
